import { DatasetType } from '@/types/automationFields.ts';
import type { CreatorPrincipalType } from '@/types/automations.ts';
import type { IntegrationInstance } from '@/types/integrations.ts';

import { PaginatedResponse } from '@/types/common.ts';
import type { StepExecution } from '@/types/stepExecutions.ts';

export type DatasetRelationshipUnavailable = {
  available: false;
  reason_code: 'no_parent' | 'no_extract_execution';
};

export type DatasetParentResponse = Dataset | DatasetRelationshipUnavailable;
export type DatasetExtractExecutionResponse = StepExecution | DatasetRelationshipUnavailable;

export const isDatasetRelationshipUnavailable = (
  response: DatasetParentResponse | DatasetExtractExecutionResponse,
): response is DatasetRelationshipUnavailable =>
  'available' in response && response.available === false;

export type Dataset = {
  id: string;
  name: string;
  dataset_type: DatasetType;
  unprocessed: boolean;
  processing_error: string | null;
  has_original_blob: boolean;
  original_mime_type: string | null;
  // is_output_dataset: boolean;
  has_parent: boolean;
  is_natual_directory: boolean;
};
export type DatasetNotes = {
  id: string;
  notes: string;
};
export type Datasets = PaginatedResponse<Dataset>;
export type DatasetPreview = {
  dataset: Dataset;
  preview_kind: string;
  columns: string[] | null;
  rows: unknown[] | null;
  value: unknown | null;
  child_datasets: Dataset[] | null;
  row_count: number;
  total_count: number;
  truncated: boolean;
  truncation_details?: { path: string; reason: string; omitted_count: number }[];
  truncation_details_truncated?: boolean;
  max_rows: number;
  max_cell_chars: number;
  error: string | null;
};
export type StaticDataset = {
  latest_ready_revision_id?: string | null;
  id: string;
  dataset: Dataset;
  uploaded_by: string;
  uploaded_by_type: CreatorPrincipalType;
  uploaded_by_display_name: string;
  uploaded_by_email: string;
  uploaded_by_label: string;
  created_at: string;
};
export type StaticDatasets = PaginatedResponse<StaticDataset>;
export type TriggerDataset = {
  id: string;
  dataset: Dataset;
  created_at: string;
  trigger_step_id: string;
  trigger_id: string;
  port_name?: string | null;
};
export type TriggerDatasets = PaginatedResponse<TriggerDataset>;

export enum SyncStatus {
  Completed = 'Completed',
  Syncing = 'Syncing',
  Error = 'Error',
}

export type LinkedDataset = {
  id: string;
  name: string;
  source_id: string;
  source_path: string;
  integration: IntegrationInstance;
  latest_dataset: Dataset | null;
  is_file: boolean;
  error_message: string;
};
export type LinkedDatasets = PaginatedResponse<LinkedDataset>;

export type StaticDatasetDependency = {
  kind: 'saved_draft' | 'published_automation' | 'run' | 'test_batch' | 'release_candidate';
  visibility: 'readable' | 'restricted' | 'unavailable';
  label: string;
  consumer_id: string | null;
  automation_id: string | null;
  status: string | null;
  version_id: string | null;
  step_id: string | null;
  step_name: string | null;
  subflow_id: string | null;
  subflow_name: string | null;
  scope: string;
};
export type StaticDatasetUsage = {
  static_dataset_id: string;
  dataset_id: string;
  name: string;
  uses: ('draft' | 'published' | 'outstanding')[];
  references: {
    kind: string;
    version_id: string | null;
    step_id: string | null;
    path: string | null;
    scope: string;
    subflow_id: string | null;
  }[];
  removal_disposition: 'blocked' | 'unknown' | 'confirm_removal';
  dependencies?: StaticDatasetDependency[];
  dependency_details_truncated?: boolean;
};
export type StaticDatasetUsageReport = {
  schema_version: string;
  automation_id: string;
  observed_at: string;
  head_revision: number;
  latest_version_id: string | null;
  published_version_id: string | null;
  active_complete: boolean;
  incomplete_reasons: string[];
  count: number;
  page: number;
  next_page: number | null;
  history_complete: boolean;
  history_next_page: number | null;
  history_errors: boolean;
  history_notice: string;
  results: StaticDatasetUsage[];
  dependencies_included?: boolean;
  inspection_issues?: string[];
};

export type StaticRevision = {
  id: string;
  source_id: string;
  status: 'pending' | 'processing' | 'sealing' | 'ready' | 'failed' | 'retired';
  snapshot_dataset_id: string | null;
  upload_dataset_id: string;
  manifest_digest: string;
  provenance: string;
  failure_code: string;
  created_at: string;
};
export type StaticVersionResources = {
  version_id: string;
  binding_state: 'resolved' | 'unresolved';
  selections: Record<string, string> | null;
  errors: Record<string, string>;
  resources: {
    source_id: string;
    revision_id: string;
    dataset_id: string;
    name: string;
    provenance: string;
  }[];
};
